import * as React from 'react';
import { Modal, message, Spin } from 'antd';
import I18N from 'src/services/I18NService';
import { ColumnProps } from 'antd/lib/table';
import PriceChangeService, { IPriceApprove, IPriceChangeParam, EnumPriceType, IPriceChangeData, IWestPriceChangeData } from 'src/services/PriceChangeService';
import { IAgentInfo } from 'src/entity/AgentEntity';
import { IDrugStoreInfo } from 'src/entity/DrugStoreEntity';
import CommonTable from './CommonTable';
import { ICommonTable } from './CommonTable';
import { EditableComponents, IEditColumnProps } from './Editable';
import * as uuid from 'uuid';
import { MddCurrency } from '@macrocura/utils';
import { IMallCPMDetail, IMallTCMDetail } from 'src/entity/MallEntity';

interface IPriceChangeTableProps {
  userInfo: IAgentInfo | IDrugStoreInfo;
  /** 选中的key列表 */
  selectedRowKeys: string[];
  /** 是否可编辑 */
  edit: boolean;
  /** 是否显示Modal */
  visible: boolean;
  /** 数据加载中Spin */
  loading: boolean;
  /** 列表数据 */
  dataSource: IMallCPMDetail[];
  /** 关闭Modal回调 */
  cbVisible(visible: boolean): void;
}

interface IPriceChangeTableState extends ICommonTable<IMallCPMDetail> {
  confirmLoading: boolean;
  columns: Array<IEditColumnProps<IMallCPMDetail>>;
  recordData: IMallCPMDetail[] | undefined;
}

class WestPriceChangeTable extends React.Component<IPriceChangeTableProps, IPriceChangeTableState> {
  constructor(props: IPriceChangeTableProps) {
    super(props);

    const currencySymbol = MddCurrency.getCurrencySymbol(this.props.userInfo.currency);
    const columns: Array<IEditColumnProps<IMallCPMDetail>> = [
      {
        title: I18N.get('med_agent_name'),
        key: 'medicine_name',
        dataIndex: 'name',
        width: 150,
        align: 'center',
      },
      {
        title: `${I18N.get('med_change_supply_price')}(${currencySymbol})`,
        key: 'supply_price',
        dataIndex: 'supply_price',
        width: 200,
        align: 'center',
        className: 'new-price',
        editable: this.props.edit,
        render: (text: any, record: IMallCPMDetail, index: number): React.ReactNode => {
          return record.supply_price;
        },
      },
      {
        title: `${I18N.get('med_change_buy_floor_price')}(${currencySymbol})`,
        key: 'floor_price',
        dataIndex: 'floor_price',
        width: 200,
        align: 'center',
        className: 'new-price',
        editable: this.props.edit,
        render: (text: any, record: IMallCPMDetail, index: number): React.ReactNode => {
          return record.floor_price
        },
      },
    ];
    this.state = {
      rowKey: 'uuid',
      recordData: [],
      confirmLoading: props.loading,
      columns,
      loadingTable: props.loading,
    };
  }



  getNewPrice = (data: IPriceApprove, type: EnumPriceType): number => {
    let newPrice = 0;
    const priceData = (data.item.details || []).find((item) => item.price_type === type);
    if (priceData) {
      newPrice = priceData.new_price;
    }
    return newPrice;
  };

  async componentDidMount() {
    await this.fetchPriceChangeData();
  }

  UNSAFE_componentWillReceiveProps(nextProps: IPriceChangeTableProps) {
    this.setState(
      {
        recordData: [],
        confirmLoading: nextProps.loading,
        loadingTable: nextProps.loading,
      },
      async () => {
        await this.fetchPriceChangeData();
      }
    );
  }

  componentWillUnmount() {
    this.setState = () => {
      return;
    };
  }

  fetchPriceChangeData = async () => {
    if (this.state.loadingTable) {
      return;
    }
    try {
      this.setState({
        loadingTable: true,
      });
      const { dataSource, selectedRowKeys } = this.props;
      if (selectedRowKeys.length > 0 && dataSource.length > 0) {

        const list: IMallCPMDetail[] = [];
        selectedRowKeys.forEach(item => {
          const find = dataSource.find(data => data.uuid === item);
          if (find) {
            list.push(find);
          }
        })
        this.setState({
          recordData: list,
          loadingTable: false,
        });
      }
    } catch (error) {
      this.setState({
        loadingTable: false,
      });
      if (!!error.response) {
        message.error(JSON.stringify(error.response.data));
      } else {
        message.error(I18N.get('err_delete_failure'));
      }
    }
  };

  handleOk = async () => {
    if (this.state.confirmLoading) {
      return;
    }
    const { recordData } = this.state;
    const priceChangeError = (recordData || []).filter((record) => {
      return (record.floor_price) > (record.supply_price);
    });
    if (priceChangeError.length > 0) {
      const errorMedicineName = priceChangeError.map((item) => item.name).join(',');
      message.error(errorMedicineName + ': ' + I18N.get('med_buy_price') + I18N.get('med_not_lower') + I18N.get('med_preserve_price'), 4);
      return;
    }
    this.setState({
      confirmLoading: true,
    });
    try {
      if (!!recordData && !!recordData.length) {
        const dataList: IWestPriceChangeData[] = [];
        for (const item of recordData) {
          dataList.push({
            sku: item.uuid,
            floor_price: item.floor_price,
            supply_price: item.supply_price,
          })
        }
        if (!!dataList.length) {
          await PriceChangeService.priceChangeWestFloorSupply(dataList);
          message.success(I18N.get('common_submit_success'));
        }
      }
    } catch (error) {
      if (!!error.response) {
        message.error(JSON.stringify(error.response.data));
      } else {
        message.error(I18N.get('err_submit_failure'));
      }
    } finally {
      this.setState({
        confirmLoading: false,
      });
      this.props.cbVisible(false);
    }
  };

  handleCancel = () => {
    this.props.cbVisible(false);
  };

  handleSave = async (row: IMallCPMDetail) => {
    try {
      if (row.hasOwnProperty('supply_price')) {
        if (!row.supply_price || Number(row.supply_price) < 0.0001) {
          message.error(I18N.get('err_amount_minus2'));
          return;
        }
      }

      if (row.hasOwnProperty('floor_price')) {
        if (!row.floor_price || Number(row.floor_price) < 0.0001) {
          message.error(I18N.get('err_amount_minus2'));
          return;
        }
      }

      if (!!this.state.recordData) {
        const index = this.state.recordData.findIndex((t) => t.uuid === row.uuid);
        this.state.recordData[index] = row;
      }
      this.setState({ recordData: this.state.recordData });
    } catch (error) {
      if (!!error.response) {
        message.error(JSON.stringify(error.response.data));
      } else {
        message.error(I18N.get('err_save_failure'));
      }
    }
  };

  render() {
    const columns: Array<ColumnProps<IMallCPMDetail>> = this.state.columns.map((col) => {
      if (!col.editable) {
        return col;
      }
      return {
        ...col,
        onCell: (record: IMallCPMDetail) => ({
          record,
          editable: col.editable,
          dataIndex: col.dataIndex,
          title: col.title,
          handleSave: this.handleSave,
        }),
      };
    });

    const dataSource = !!this.state.recordData ? this.state.recordData : [];

    const { loading } = this.props;
    const { loadingTable } = this.state;
    
    return (
      <Modal
        title={'成药' + I18N.get('med_change_price')}
        okText={I18N.get('common_submit')}
        cancelText={I18N.get('common_cancel')}
        onOk={this.handleOk}
        confirmLoading={this.state.confirmLoading}
        onCancel={this.handleCancel}
        closable={false}
        centered={true}
        visible={this.props.visible}
        maskClosable={false}
        width={1000}
        key="price-change-modal"
      >
        <Spin tip="加载中..." spinning={loading}>
          <CommonTable
            rowKey={this.state.rowKey}
            showPagination={false}
            loadingTable={loadingTable}
            components={EditableComponents}
            rowClassName={() => 'editable-row'}
            columns={columns}
            dataSource={dataSource}
            scroll={{ y: 420 }}
          // locale={{
          //   emptyText: `${I18N.get('med_change_no_have')}`,
          // }}
          />
        </Spin>
      </Modal>
    );
  }
}

export default WestPriceChangeTable;



// WEBPACK FOOTER //
// ./src/components/common/WestPriceChangeTable.tsx